<script>
  // Application shell. Renders the global Header + Sidebar around the page
  // slot. Replaces the SvelteKit +layout.svelte cascade.
  //
  //   - Embed routes (/embed/...) render <slot/> bare (no chrome).
  //   - The /login route renders the page in a borderless container.
  //   - All other routes render Header + Sidebar around the page.
  //
  // The shell renders Header/Sidebar only when the user is authenticated, so
  // dropping it inside an <AuthGuard> is harmless but unnecessary.

  import { onMount, onDestroy } from 'svelte';
  import { page } from '../stores/pageStore.js';
  import { theme } from '../stores/themeStore.js';
  import { sidebarCollapsed } from '../stores/uiStore.js';
  import { isAuthenticated } from './auth.js';
  import Header from './Header.svelte';
  import Sidebar from './Sidebar.svelte';
  import { setupViewportHandler } from './viewportHandler.js';

  let cleanupViewport;

  $: pathname = $page.url.pathname;
  $: isEmbedRoute = pathname.startsWith('/embed');
  $: isLoginRoute = pathname === '/login';

  onMount(() => {
    // Sync the `dark` / `light` class on <html>.
    const unsubscribeTheme = theme.subscribe((value) => {
      if (value) document.documentElement.classList.add(value);
    });

    if (typeof window !== 'undefined') {
      cleanupViewport = setupViewportHandler();
    }

    return () => unsubscribeTheme();
  });

  onDestroy(() => {
    if (cleanupViewport) cleanupViewport();
  });
</script>

{#if isEmbedRoute}
  <slot />
{:else}
  <div class="main-container">
    {#if $isAuthenticated}
      <Header />
    {/if}
    <div class="content-container">
      {#if $isAuthenticated}
        <Sidebar />
      {/if}
      <div
        class="content bg-white dark:bg-gray-700"
        class:login-content={isLoginRoute}
        style="margin-left: {$sidebarCollapsed ? '52px' : '208px'}; margin-top: 44px;"
      >
        <slot />
      </div>
    </div>
  </div>
{/if}

<style>
  .main-container {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }
  .content-container {
    display: flex;
    flex: 1;
    overflow: visible;
    min-height: 0;
  }
  .content {
    flex: 1;
    padding: 0.5rem;
    overflow-y: auto;
    min-height: 0;
  }
  .content.login-content {
    padding: 0;
  }

  @media print {
    .main-container {
      height: auto;
      overflow: visible;
    }
    .content-container {
      overflow: visible;
      min-height: auto;
    }
    .content {
      overflow: visible;
      min-height: auto;
      height: auto;
    }
  }
</style>
